import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ExternalLink, Eye, Copy, CheckCircle } from 'lucide-react';

const SHOPIFY_VARIANTS: Record<string, Record<string, string>> = {
  'yh-680b-apap-n20': { S: '46770278203604', M: '46770273616084', L: '46770273648852' },
  'airsense-10-apap-yf-02': { S: '46690770878676', M: '46690770911444', L: '46690770944212' },
  'airsense-10-apap-yn-03': { S: '46690770780372', M: '46690770813140', L: '46690770845908' },
  'airsense-10-apap-f20': { S: '46690770682068', M: '46690770714836', L: '46690770747604' },
  'airsense-10-apap-n20': { S: '46690770583764', M: '46690770616532', L: '46690770649300' },
  'yh-680b-apap-yf-02': { S: '46690770485460', M: '46690770518228', L: '46690770550996' },
  'yh-680b-apap-yn-03': { S: '46624737427668', M: '46624736870612', L: '46624737460436' }
};

const BUNO_PLUS_VARIANT_ID = '47561280979156';
const SHOPIFY_STORE_URL = 'https://m650id-ee.myshopify.com';

const generateShopifyCartUrl = (bundleId: string, maskSize: string, includeBuno: boolean): string | null => {
  try {
    const variantMapping = SHOPIFY_VARIANTS[bundleId];
    
    if (!variantMapping) {
      console.error(`No Shopify variant mapping found for bundle: ${bundleId}`);
      return null;
    }
    
    if (!variantMapping[maskSize]) {
      console.error(`No variant found for bundle: ${bundleId}, size: ${maskSize}. Available sizes:`, Object.keys(variantMapping));
      return null;
    }
    
    const productVariantId = variantMapping[maskSize];
    const cartItems = [`${productVariantId}:1`];
    
    if (includeBuno) {
      cartItems.push(`${BUNO_PLUS_VARIANT_ID}:1`);
    }
    
    const cartUrl = `${SHOPIFY_STORE_URL}/cart/${cartItems.join(',')}`;
    console.log(`Generated Shopify cart URL: ${cartUrl}`);
    return cartUrl;
  } catch (error) {
    console.error('Error generating Shopify cart URL:', error);
    return null;
  }
};

const getProductDisplayName = (bundleId: string, size: string) => {
  const parts = bundleId.split('-');
  const equipment = parts.includes('airsense') ? 'AirSense 10 APAP' : 'YH-680B APAP';
  const mask = parts.includes('f20') ? 'F20' : 
               parts.includes('n20') ? 'N20' : 
               parts.includes('yf-02') ? 'YF-02' : 'YN-03';
  
  return `${equipment} + ${mask} (Size ${size})`;
};

export default function VariantTester() {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const testVariant = (bundleId: string, size: string, includeBuno: boolean = false) => {
    const shopifyUrl = generateShopifyCartUrl(bundleId, size, includeBuno);
    
    if (shopifyUrl) {
      window.open(shopifyUrl, '_blank');
    } else {
      alert('Failed to generate Shopify cart URL');
    }
  };

  const previewVariant = (bundleId: string, size: string, includeBuno: boolean = false) => {
    const shopifyUrl = generateShopifyCartUrl(bundleId, size, includeBuno);
    const title = `${getProductDisplayName(bundleId, size)}${includeBuno ? ' + Buno Plus' : ''}`;
    
    if (shopifyUrl) {
      setPreviewUrl(shopifyUrl);
      setPreviewTitle(title);
    } else {
      alert('Failed to generate Shopify cart URL');
    }
  };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            Shopify Variant Tester
          </CardTitle>
          <p className="text-muted-foreground">
            Click any button to test the Shopify cart integration with that specific variant.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {Object.entries(SHOPIFY_VARIANTS).map(([bundleId, sizes]) => (
            <div key={bundleId} className="space-y-3">
              <h3 className="font-medium text-lg capitalize">
                {bundleId.replace(/-/g, ' ')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(sizes).map(([size, variantId]) => (
                  <div key={`${bundleId}-${size}`} className="space-y-2">
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        className="flex-1 justify-between"
                        onClick={() => testVariant(bundleId, size, false)}
                      >
                        <span className="text-left text-xs">
                          {getProductDisplayName(bundleId, size)}
                        </span>
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="px-2"
                            onClick={() => previewVariant(bundleId, size, false)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Cart URL Preview</DialogTitle>
                            <DialogDescription>
                              {previewTitle} - Copy the URL below to test manually
                            </DialogDescription>
                          </DialogHeader>
                          {previewUrl && (
                            <div className="space-y-4">
                              <div className="p-4 bg-muted rounded-lg">
                                <p className="text-sm text-muted-foreground mb-2">Generated Shopify Cart URL:</p>
                                <code className="text-sm break-all block p-2 bg-background rounded border">
                                  {previewUrl}
                                </code>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  className="flex-1"
                                  onClick={() => copyToClipboard(previewUrl)}
                                >
                                  {copiedUrl === previewUrl ? (
                                    <>
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="h-4 w-4 mr-2" />
                                      Copy URL
                                    </>
                                  )}
                                </Button>
                                <Button onClick={() => window.open(previewUrl, '_blank')}>
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  Open Cart
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => testVariant(bundleId, size, true)}
                      >
                        + Buno Plus
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="px-2"
                            onClick={() => previewVariant(bundleId, size, true)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Cart URL Preview</DialogTitle>
                            <DialogDescription>
                              {previewTitle} - Copy the URL below to test manually
                            </DialogDescription>
                          </DialogHeader>
                          {previewUrl && (
                            <div className="space-y-4">
                              <div className="p-4 bg-muted rounded-lg">
                                <p className="text-sm text-muted-foreground mb-2">Generated Shopify Cart URL:</p>
                                <code className="text-sm break-all block p-2 bg-background rounded border">
                                  {previewUrl}
                                </code>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  className="flex-1"
                                  onClick={() => copyToClipboard(previewUrl)}
                                >
                                  {copiedUrl === previewUrl ? (
                                    <>
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="h-4 w-4 mr-2" />
                                      Copy URL
                                    </>
                                  )}
                                </Button>
                                <Button onClick={() => window.open(previewUrl, '_blank')}>
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  Open Cart
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      Variant ID: {variantId}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          <div className="border-t pt-4">
            <h3 className="font-medium text-lg mb-3">Buno Plus Only</h3>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  const cartUrl = `${SHOPIFY_STORE_URL}/cart/${BUNO_PLUS_VARIANT_ID}:1`;
                  window.open(cartUrl, '_blank');
                }}
              >
                Test Buno Plus Only
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const cartUrl = `${SHOPIFY_STORE_URL}/cart/${BUNO_PLUS_VARIANT_ID}:1`;
                      setPreviewUrl(cartUrl);
                      setPreviewTitle('Buno Plus Only');
                    }}
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl w-full h-[80vh]">
                  <DialogHeader>
                    <DialogTitle>{previewTitle}</DialogTitle>
                  </DialogHeader>
                  {previewUrl && (
                    <iframe
                      src={previewUrl}
                      className="w-full h-full border-0 rounded"
                      title={previewTitle}
                    />
                  )}
                </DialogContent>
              </Dialog>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Variant ID: {BUNO_PLUS_VARIANT_ID}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}